'use client'

import { LayoutGrid, type LucideIcon } from 'lucide-react'
import { Fragment, memo, useMemo } from 'react'

import { navSlots, type NavGroupName, type NavItem, type NavSlot } from './navConfig'
import { isRedirectHref, isSectionActive } from './NavLink'
import { NavBarLink, NavMenuItem, NavMenuTrigger, navMenuContentCSS } from './NavMenu'
import { useHoverMenu } from './useHoverMenu'
import { useNavOverflow } from './useNavOverflow'
import { HOTKEY_BY_PATH } from '@features/appHotkeys/appHotkeys'
import { Kbd } from '@fp/ui-kit'
import { cn } from '@lib/cn'
import { commonIconProps } from '@lib/iconProps'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ui/DropDownMenu'

const groupLabelCSS = 'flex items-center gap-2 py-1 text-xs font-normal text-muted-foreground'

const findActiveTitle = (items: NavItem[], pathname: string) =>
  items.find((item) => isSectionActive(pathname, item.href))?.title

const slotItems = (slot: NavSlot) => (slot.kind === 'link' ? [slot.item] : slot.items)

const triggerLabel = (title: string, activeTitle?: string) =>
  activeTitle ? <span className='font-medium'>{activeTitle}</span> : title

type NavGroupMenuProps = { title: NavGroupName; icon: LucideIcon; items: NavItem[]; pathname: string }

const NavGroupMenu: React.FC<NavGroupMenuProps> = memo(({ title, icon, items, pathname }) => {
  const { rootProps, triggerProps, contentProps } = useHoverMenu()
  const activeTitle = findActiveTitle(items, pathname)

  return (
    <DropdownMenu {...rootProps}>
      <DropdownMenuTrigger asChild>
        <NavMenuTrigger
          icon={icon}
          isActive={!!activeTitle}
          label={triggerLabel(title, activeTitle)}
          {...triggerProps}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start' sideOffset={0} className={navMenuContentCSS} {...contentProps}>
        {items.map((item) => (
          <NavMenuItem key={item.href} href={item.href} title={item.title} icon={item.icon} pathname={pathname} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
})
NavGroupMenu.displayName = 'NavGroupMenu'

type MoreSlotsMenuProps = { slots: NavSlot[]; pathname: string }

const MoreSlotsMenu: React.FC<MoreSlotsMenuProps> = memo(({ slots, pathname }) => {
  const { rootProps, triggerProps, contentProps } = useHoverMenu()
  const activeTitle = findActiveTitle(slots.flatMap(slotItems), pathname)

  return (
    <DropdownMenu {...rootProps}>
      <DropdownMenuTrigger asChild>
        <NavMenuTrigger
          icon={LayoutGrid}
          isActive={!!activeTitle}
          label={triggerLabel('More', activeTitle)}
          aria-label='More pages'
          {...triggerProps}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start' sideOffset={0} className={navMenuContentCSS} {...contentProps}>
        {slots.map((slot, index) =>
          slot.kind === 'link' ? (
            <NavMenuItem
              key={slot.item.href}
              href={slot.item.href}
              title={slot.item.title}
              icon={slot.item.icon}
              pathname={pathname}
            />
          ) : (
            <Fragment key={slot.title}>
              {index > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel className={groupLabelCSS}>
                <slot.icon {...commonIconProps} className='size-3.5 shrink-0' />
                {slot.title}
              </DropdownMenuLabel>
              {slot.items.map((item) => (
                <NavMenuItem key={item.href} href={item.href} title={item.title} icon={item.icon} pathname={pathname} />
              ))}
            </Fragment>
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
})
MoreSlotsMenu.displayName = 'MoreSlotsMenu'

type GroupedNavProps = { pathname: string; showHotkeys: boolean }

export const GroupedNav: React.FC<GroupedNavProps> = memo(({ pathname, showHotkeys }) => {
  const { navRef, moreRef, registerItem, visibleCount } = useNavOverflow({
    itemCount: navSlots.length,
    gap: 2, // the nav's `gap-0.5`
    remeasureKey: pathname,
  })

  const overflowSlots = useMemo(() => navSlots.slice(visibleCount), [visibleCount])

  return (
    <nav ref={navRef} className='mr-4 h-20 flex flex-1 min-w-0 items-center justify-start gap-0.5 overflow-hidden'>
      {navSlots.slice(0, visibleCount).map((slot, index) => {
        if (slot.kind === 'group') {
          return (
            <div key={slot.title} ref={registerItem(index)}>
              <NavGroupMenu title={slot.title} icon={slot.icon} items={slot.items} pathname={pathname} />
            </div>
          )
        }

        const { title, href } = slot.item
        const isRedirect = isRedirectHref(href)
        const hotkey = HOTKEY_BY_PATH[href]

        return (
          <div key={href} ref={registerItem(index)} className='relative'>
            <NavBarLink href={href} title={title} pathname={pathname} />

            {showHotkeys && !isRedirect && hotkey && (
              <Kbd size='sm' className='absolute -bottom-5 left-1/2 -translate-x-1/2'>
                {hotkey}
              </Kbd>
            )}
          </div>
        )
      })}

      {/* Kept mounted so its width stays in the overflow measurement — hiding it would let items oscillate. */}
      <div ref={moreRef} className={cn(!overflowSlots.length && 'invisible pointer-events-none')}>
        <MoreSlotsMenu slots={overflowSlots} pathname={pathname} />
      </div>
    </nav>
  )
})
GroupedNav.displayName = 'GroupedNav'
